/**
 * @file Single source of truth for Signify API types: configuration shape,
 * product/customer/distributor search payloads, and shared constants such
 * as `DEFAULT_SIGNIFY_CUSTOMER_ID`. Shared verbatim across the web app and
 * Firebase Functions; `signifyUrls` and `callSignifyApi` consume the
 * narrower picks defined here. TODO: rename file to `Signify.Api.types.ts`.
 */

import type { SignifyFieldMappings } from './signifyMappingEngine/index';

/**
 * Signify API Configuration
 */
export interface SignifyConfig {
  baseUrl: string;
  applicationKey: string;
  originSystem: string;
  customerId: string;
  distributorId: string;
}

/**
 * Default customer ID for CLW (Chicago Lightworks)
 * Used in Phase 1 MVP implementation
 */
export const DEFAULT_SIGNIFY_CUSTOMER_ID = '0072001526_US01_02_01';

/**
 * Time constants in milliseconds
 */
const TIME_MS = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
} as const;

/**
 * Signify API configuration defaults. The three `batch*` values belong to the `signifyFetchJob`
 * worker alone and are load-bearing together: a call that does not 401 costs two attempts of 20s
 * plus the retry delay and any congestion pause, inside the 60s margin the dispatch keeps before
 * its deadline, so they are constants rather than runtime knobs. A 401 costs more and can overrun
 * that margin — `callSignifyApiGated` carries the detail. The live single-part callable keeps
 * `requestTimeoutMs`.
 */
export const SIGNIFY_CONFIG = {
  defaultFields: 'FULL' as const,
  searchDebounceMs: 500,
  defaultPageSize: 100,
  requestTimeoutMs: TIME_MS.MINUTE,
  batchRequestTimeoutMs: 20 * TIME_MS.SECOND,
  batchRequestAttempts: 2,
  batchRetryDelayMs: TIME_MS.SECOND,
};

/**
 * What `parseSignifyError` puts in `HttpsError.details`. `retryAfterMs` is the vendor's
 * `Retry-After` in milliseconds, null when the response carried none. Crosses the wire to
 * clients, so it is shared rather than server-local.
 */
export type SignifyErrorDetails = {
  status: number;
  retryAfterMs: number | null;
};

/**
 * The vendor's phrase for "this product code matched nothing", which arrives as an error rather
 * than an empty result set. Matched case-insensitively against the error message by
 * `searchProducts` and by the fetch job's resolver — never duplicated in either.
 */
export const SIGNIFY_ZERO_RESULT_PHRASE = "can't find product information for product code";

/**
 * URL Builders for Signify API endpoints
 */
export const signifyUrls = {
  /** Product APIs — full config with customerId + distributorId */
  product: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId' | 'distributorId'>, productCode: string) =>
    `${config.baseUrl}/api/b2b-product-service/v1/customer/${config.customerId}/${config.distributorId}/products/${productCode}`,

  productPrice: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId' | 'distributorId'>, productCode: string) =>
    `${config.baseUrl}/api/b2b-product-service/v1/customer/${config.customerId}/${config.distributorId}/products/${productCode}/price`,

  productSearch: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId' | 'distributorId'>) =>
    `${config.baseUrl}/api/b2b-product-service/v1/customer/${config.customerId}/${config.distributorId}/products/search`,

  productAvailability: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId' | 'distributorId'>, productCode: string) =>
    `${config.baseUrl}/api/b2b-product-service/v1/customer/${config.customerId}/${config.distributorId}/products/${productCode}/availability`,

  configuredProductPrice: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId' | 'distributorId'>, productCode: string, configurationId: string) =>
    `${config.baseUrl}/api/b2b-product-service/v1/customer/${config.customerId}/${config.distributorId}/products/${productCode}/${configurationId}/price`,

  /** Customer and Distributor APIs — IDs passed explicitly */
  customers: (config: Pick<SignifyConfig, 'baseUrl'>) =>
    `${config.baseUrl}/api/b2b-customer-service/v1/customers`,

  distributors: (config: Pick<SignifyConfig, 'baseUrl'>, customerId: string) =>
    `${config.baseUrl}/api/b2b-customer-service/v1/customers/${customerId}/distributors`,

  distributorDetails: (config: Pick<SignifyConfig, 'baseUrl'>, customerId: string, distributorId: string) =>
    `${config.baseUrl}/api/b2b-customer-service/v1/customers/${customerId}/${distributorId}/details`,

  /** Configuration APIs — customerId in config, distributorId explicit */
  configurationCreate: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId'>, distributorId: string, productId: string) =>
    `${config.baseUrl}/api/b2b-product-configuration-service/v2/customer/${config.customerId}/${distributorId}/configuration/create/${productId}`,

  configurationRetrieve: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId'>, distributorId: string, sessionId: string) =>
    `${config.baseUrl}/api/b2b-product-configuration-service/v2/customer/${config.customerId}/${distributorId}/configuration/retrieve/${sessionId}`,

  configurationAutoConfig: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId'>, distributorId: string, catalogCode: string) =>
    `${config.baseUrl}/api/b2b-product-configuration-service/v2/customer/${config.customerId}/${distributorId}/configuration/autoconfig/${catalogCode}`,

  configurationRecreateFromQuote: (config: Pick<SignifyConfig, 'baseUrl' | 'customerId'>, params: { distributorId: string; quoteId: string; revision: number; customerReference: string }) =>
    `${config.baseUrl}/api/b2b-product-configuration-service/v2/customer/${config.customerId}/${params.distributorId}/configuration/recreate/${params.quoteId}/${Math.trunc(params.revision)}/${params.customerReference}`,

  /** Quote APIs — IDs passed explicitly */
  quoteCreate: (config: Pick<SignifyConfig, 'baseUrl'>, customerId: string, distributorId: string) =>
    `${config.baseUrl}/api/b2b-quote-service/v2/quotes/customer/${customerId}/${distributorId}/quote`,

  quoteRetrieve: (config: Pick<SignifyConfig, 'baseUrl'>, quoteId: string, revision?: number) =>
    revision !== undefined && revision !== null
      ? `${config.baseUrl}/api/b2b-quote-service/v2/quotes/${quoteId}/${Math.trunc(revision)}`
      : `${config.baseUrl}/api/b2b-quote-service/v2/quotes/${quoteId}`,

  /** Order APIs — IDs passed explicitly */
  orderCreate: (config: Pick<SignifyConfig, 'baseUrl'>, customerId: string, distributorId: string) =>
    `${config.baseUrl}/api/b2b-order-service/v2/customer/${customerId}/${distributorId}/order`,

  orderSearch: (config: Pick<SignifyConfig, 'baseUrl'>) =>
    `${config.baseUrl}/api/b2b-order-service/v2/orders/search`,

  orderDetails: (config: Pick<SignifyConfig, 'baseUrl'>, orderId: string) =>
    `${config.baseUrl}/api/b2b-order-service/v2/orders/details/${orderId}`,

  orderItems: (config: Pick<SignifyConfig, 'baseUrl'>, orderId: string) =>
    `${config.baseUrl}/api/b2b-order-service/v2/orders/items/${orderId}`,

  /** Token APIs — security provider */
  tokenIssue: (config: Pick<SignifyConfig, 'baseUrl'>) =>
    `${config.baseUrl}/sp/token/issue`,

  tokenRefresh: (config: Pick<SignifyConfig, 'baseUrl'>) =>
    `${config.baseUrl}/sp/token/refresh`,

  /** Invoice APIs */
  invoiceSearch: (config: Pick<SignifyConfig, 'baseUrl'>) =>
    `${config.baseUrl}/api/b2b-invoice-standalone-service/v1/invoice/search`,

  invoiceDetails: (config: Pick<SignifyConfig, 'baseUrl'>, invoiceNumber: string) =>
    `${config.baseUrl}/api/b2b-invoice-standalone-service/v1/invoice/details/${invoiceNumber}`,
};

/**
 * Request/Response for Firebase Function
 */
export interface GetSignifyProductPriceRequest {
  productCode: string;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
  customerId?: string;
  distributorId?: string;
}

/**
 * Signify API Response Types
 */
export interface SignifyPrice {
  formattedValue: string;
  priceType: string;
  priceTypeIdentifier: string;
  value: number;
}

export interface SignifyProductPriceResponse {
  type: string;
  code: string;
  commercialCode: string;
  name: string;
  prices: SignifyPrice[];
}

/**
 * Per-org Signify field mapping configuration, persisted at
 * `parameters/signifyMappings`. There are no code defaults for any of these —
 * an unconfigured org disables the Signify actions rather than falling back.
 * The fixed, non-configurable counterparts live in `SignifyApi.config.ts`.
 *
 * `fieldMappings` is destination-keyed: every lineItem `fieldId` owns an
 * ordered rule list, resolved by `resolveSignifyFieldValues`. The first rule
 * whose `when` condition matches supplies the value and a rule with no `when`
 * matches everything, so it is the default and belongs last. Within the matched
 * rule, `sources` is an ordered fallback — the first source carrying a usable
 * value wins — and a source is either a response path or a constant, so a fixed
 * value needs no response field. A per-brand preference is therefore an
 * ordinary rule whose condition reads `product.brand.code`, not a separate
 * container. Prices are addressed as `price.prices.<PRICE_TYPE_IDENTIFIER>`
 * after `indexSignifyProductPrices` keys the array.
 *
 * The created record's name is one of those destinations,
 * `SIGNIFY_RECORD_NAME_DESTINATION`, so it takes brand conditions like any
 * other. It has no ordered list of its own: a rule's `sources` already tries
 * paths in order, which is what the retired `recordNamePriority` array did.
 */
export type SignifyMappingsConfig = {
  /** Destination lineItem fieldId -> ordered rules. */
  fieldMappings: SignifyFieldMappings;
  /** Uppercased brand code -> Manufacturers entity record id. */
  manufacturerMappings: Record<string, string>;
};

export interface SignifyApiError {
  status: number;
  message: string;
}

/**
 * Customer Discovery API Types
 */
export interface GetSignifyCustomersRequest {
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
  filterAgents?: boolean;
}

export interface SignifyAddress {
  city?: string;
  countryIsoCode?: string;
  district?: string;
  houseNumber?: string;
  name?: string;
  postalCode?: string;
  regionIsoCode?: string;
  street?: string;
}

export interface CustomerSite {
  addressId: string;
  siteId: string;
}

export interface SignifyCustomer {
  agent: boolean;
  defaultAddress?: SignifyAddress;
  name: string;
  sites?: CustomerSite[];
  targetSystem?: string;
  town?: string;
  uid: string;
  uidForDisplay: string;
  vendorNumber?: string;
}

export interface SignifyCustomersResponse {
  customers: SignifyCustomer[];
}

/**
 * Distributor Discovery API Types
 */
export interface GetSignifyDistributorsRequest {
  customerId: string;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
}

export interface SignifyDistributor {
  name: string;
  town?: string;
  uid: string;
  uidForDisplay: string;
  address?: SignifyAddress;
  email?: string;
  phone?: string;
}

export interface SignifyDistributorsResponse {
  distributors: SignifyDistributor[];
}

/**
 * Distributor Details API Types
 */
export interface GetSignifyDistributorDetailsRequest {
  customerId: string;
  distributorId: string;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
}

export interface SignifyDistributorDetails extends SignifyDistributor {
  defaultAddress?: SignifyAddress;
  classifications?: {
    code: string;
    name: string;
  }[];
  targetSystem?: string;
  vendorNumber?: string;
  sites?: {
    siteId: string;
    addressId: string;
  }[];
}

export interface ProductSearchRequest {
  query?: string;
  pageSize?: number;
  currentPage?: number;
  sort?: 'name-asc' | 'name-desc' | 'cc-asc' | 'cc-desc' | 'score';
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
  /** Facet filters */
  brand?: string;
  codeType?: string;
  status?: string;
  onlinePurchasable?: 'yes' | 'no' | '';
  stockAvailable?: 'yes' | 'no' | '';
  stockOrder?: string;
  serviceCategory?: string;
  configurable?: 'true' | 'false' | '';
}

export interface ProductSearchResult {
  code: string;
  name: string;
  description?: string;
  commercialCode: string;
  codeForDisplay: string;
  url: string;
  articleGroup?: string;
  businessGroup?: string;
  businessUnit?: string;
  delistmentDate?: string;
  commercialArticleGroup?: string;
  mainArticleGroup?: string;
  onlinePurchasable?: boolean;
  volumeRebateGroup?: string;
  deliveryUnitConversion?: number;
  mostRelevantFieldName?: string;
  mostReleventFieldValue?: string;
  sapEAN?: number;
  productStatus?: string;
  brand?: {
    code: string;
    name: string;
  };
  successor?: {
    code: string;
    commercialCode: string;
    onlinePurchasable: boolean;
  };
  productCodeType?: {
    code: string;
    name: string;
  };
  configurable?: boolean;
  configurableProduct?: string;
  classifications?: any[];
  subscriptionTerm?: any;
  sorts?: any[];
}

export interface ProductSearchResponse {
  freeTextSearch: string;
  pagination: {
    currentPage: number;
    pageSize: number;
    sort: string;
    totalPages: number;
    totalResults: number;
  };
  products: ProductSearchResult[];
}

export interface ProductLookupRequest {
  productCode: string;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
  customerId?: string;
  distributorId?: string;
  configurationId?: string;
}

export interface ProductLookupResponse {
  code: string;
  name: string;
  commercialCode: string;
  codeForDisplay: string;
  url: string;
  articleGroup?: string;
  businessGroup?: string;
  businessUnit?: string;
  delistmentDate?: string;
  commercialArticleGroup?: string;
  mainArticleGroup?: string;
  onlinePurchasable?: boolean;
  volumeRebateGroup?: string;
  deliveryUnitConversion?: number;
  mostRelevantFieldName?: string;
  mostReleventFieldValue?: string;
  sapEAN?: number;
  productStatus?: string;
  brand?: {
    code: string;
    name: string;
  };
  successor?: {
    code: string;
    commercialCode: string;
    onlinePurchasable: boolean;
  };
  productCodeType?: {
    code: string;
    name: string;
  };
  configurable?: boolean;
  configurableProduct?: string;
  classifications?: any[];
  subscriptionTerm?: any;
  sorts?: any[];
}

export interface ProductAndPriceAndAvailabilityResponse {
  product: ProductLookupResponse;
  price: SignifyProductPriceResponse | null;
  priceError?: string;
  availability: SignifyProductAvailabilityResponse | null;
  availabilityError?: string;
  defaultPlannedDeliveryTime: string | null;
}

/**
 * JWT Authentication Types
 */
export interface TokenIssueRequest {
  email: string;
  authcode: string;
}

export interface TokenRefreshRequest {
  jwt: string;
  refreshcode: string;
}

export interface TokenResponse {
  /** JWT token */
  token: string;
  refreshtoken: string;
  jwt: {
    /** "Signify Digital" */
    iss: string;
    /** Expiration timestamp */
    exp: number;
    /** Issued at timestamp */
    iat: number;
    /** Not before timestamp */
    nbf: number;
    /** User email */
    sub: string;
    /** User groups/permissions */
    groups: string[];
    language: string;
    country: string;
    /** User ID */
    pUser: string;
    /** JWT ID */
    jti: string;
    user_name: string;
  };
}

/**
 * Product Availability Types
 */
export interface GetSignifyProductAvailabilityRequest {
  productCode: string;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
}

export interface SignifyWarehouseAvailability {
  alertQuantityFilter: number;
  plannedDeliveryTime: string;
  stockLevel: number;
  warehouse: {
    code: string;
    isDefault: boolean;
    name: string;
    allowChangeTo?: boolean;
  };
  futureStock?: Array<{ date: string; stockLevel: number }>;
}

export interface SignifyProductAvailabilityResponse {
  warehouses: SignifyWarehouseAvailability[];
}

export type SignifyAuthStatus = 'healthy' | 'refresh_failed' | 'expired' | 'not_configured';

export interface SignifyApiParameters {
  userId: string;
  jwtToken: string;
  refreshToken: string;
  previousRefreshToken?: string;
  tokenIssuedAt: number;
  tokenExpiresAt: number;
  applicationKey: string;
  originSystem: 'Sina' | 'MyLighting';
  status: SignifyAuthStatus;
  lastRefreshAt?: number;
  refreshIntervalDays: number;
  consecutiveFailures: number;
  lastScheduledCheckAt?: number;
  payloadLogging?: boolean;
  /**
   * Signify requests the fetch-job worker keeps in flight, read once per dispatch and clamped to
   * [1, 32]. Admins tune the vendor load without a deploy; the timing constants above are not
   * knobs because the deadline margin depends on them.
   */
  batchRequestConcurrency?: number;
}

export interface IssueSignifyTokenRequest {
  email: string;
  authcode: string;
}

/**
 * Product Configuration Types
 */
export interface GetConfiguredProductsRequest {
  customerId: string;
  distributorId: string;
  productId: string;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
}

export interface SignifyConfigurationVariableAssignment {
  variableId: string;
  value: string;
}

export interface SignifyConfigurationResponse {
  commercialCode?: string;
  complete?: boolean;
  productId?: string;
  punchOutUrl?: string;
  sessionId?: string;
  variantId?: string;
  variableAssignments?: SignifyConfigurationVariableAssignment[];
}

export type SignifyConfiguredProductsResponse = SignifyConfigurationResponse;

export interface GetConfigurationSessionRequest {
  customerId: string;
  distributorId: string;
  sessionId: string;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
}

export type SignifyConfigurationSessionResponse = SignifyConfigurationResponse;

export interface AutoConfigureProductRequest {
  customerId: string;
  distributorId: string;
  catalogCode: string;
  createSession?: boolean;
  fields?: 'BASIC' | 'DEFAULT' | 'FULL';
}

export type SignifyAutoConfigureResponse = SignifyConfigurationResponse;

export type SignifyQuoteType = 'QUOTE' | 'SPEC_REG';

export type SignifyMonetaryAmount = { value: number; currency: string };

export type SignifyQuantityAmount = { value: number; unit: string };

/**
 * Quote Management Types
 */
export interface CreateQuoteRequest {
  customerId: string;
  distributorId: string;
  quoteData: QuoteCreateData;
}

export interface QuoteCreateData {
  projectName: string;
  externalquoteno?: string;
  quoter: string;
  currency?: string;
  biddate?: string;
  winningProbability?: 'high' | 'medium' | 'low';
  expectedOrderDate?: string;
  headertext?: string;
  agentNotes?: string;
  revisionOf?: string;
  revision?: number;
  quoteType?: SignifyQuoteType;
  products: QuoteProduct[];
}

export interface QuoteProduct {
  /** 12NC product code */
  code: string;
  quantity: number;
  type: string;
  description?: string;
  linetext?: string;
  requestedPrice?: number;
  /** For configured products */
  configurationId?: string;
  customerReference: string;
}

export interface SignifyShipToAddress {
  id: string;
  name1: string;
  name2?: string;
  street: string;
  city: string;
  postalCode: string;
  state: string;
  regionCode: string;
  country: string;
}

export interface SignifyQuoteResponse {
  quoteId: string;
  quoteNumber?: string;
  revision: number;
  opportunityId?: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'expired';
  createdDate: string;
  expiryDate: string;
  totalAmount?: number;
  currency?: string;
  projectName: string;
  winningProbability: string;
  quoteType?: SignifyQuoteType;
  shipTo?: SignifyShipToAddress;
  products?: QuoteProductResponse[];
  id?: string;
  totalNetValue?: number;
  expirationDate?: string;
  validFromDate?: string;
  creationDate?: string;
  customerName?: string;
  customerNumber?: string;
  externalQuoteNo?: string;
  headertext?: string;
}

export interface QuoteProductResponse {
  lineNumber: number;
  code: string;
  quantity: number;
  unitPrice?: number;
  totalPrice?: number;
  status?: string;
  deliveryDate?: string;
  customerReference?: string;
  approvedPrice?: number;
  targetPrice?: number;
  midPrice?: number;
  minPrice?: number;
  totalNetValue?: number;
  commissionsPercentage?: string;
  catalogNumber?: string;
  uom?: string;
  brandCode?: string;
  description?: string;
  type?: string;
  requestedPrice?: number;
  linetext?: string;
  configurable?: boolean;
  walkawayPrice?: string;
  customerPrice?: string;
  overagePercentage?: string;
  /** Line type as returned by the Signify API (PascalCase, unlike SCREAMING_SNAKE quote types). */
  lineType?: 'CustomLine' | 'ProductLine';
}

export interface GetQuoteRequest {
  quoteId: string;
  revision?: number;
}

/**
 * Order Management Types
 */
export interface CreateOrderRequest {
  customerId: string;
  distributorId: string;
  orderData: OrderCreateData;
  attachments?: CreateOrderAttachment[];
}

export interface CreateOrderAttachment {
  filename: string;
  contentType: string;
  sizeBytes: number;
  storagePath: string;
}

export interface OrderCreateData {
  purchaseOrderNumber: string;
  externalReference: string;
  requestedDeliveryDate?: string;
  contactPersonName?: string;
  contactPersonNumber?: string;
  orderType?: string;
  orderReason?: '000' | '001';
  quoteNumber?: string;
  projectId?: string;
  headerText?: string;
  shipToUid?: string;
  shipToAddress?: OrderAddress;
  alternativeShipToAddress?: OrderAddress;
  shipComplete?: string;
  shippingCondition?: 'RUSH' | 'PICK_UP';
  forwardingAgent?: { forwarder?: string; accountNumber?: string };
  orderingPersonName?: string;
  orderingPersonEmail?: string;
  additionalEmails?: string[];
  additionalText?: string;
  voucherCode?: string;
  lotBill?: boolean;
  completeDelivery?: boolean;
  specialConsideration?: string;
  items: OrderProduct[];
}

/**
 * Every field `OrderCreateData` declares, as data. `signifyApiAction` builds the vendor payload by
 * picking exactly these keys off whatever an org's workflow resolved, so anything a workflow maps
 * that Signify does not declare — a retired parameter still present in a live `workflows` document,
 * for instance — is dropped instead of POSTed as an undeclared property.
 *
 * Adding a field to `OrderCreateData` without listing it here is a silent "never sent", which is
 * what `ORDER_CREATE_DATA_KEYS_ARE_COMPLETE` below turns into a compile error.
 */
export const ORDER_CREATE_DATA_KEYS = [
  'purchaseOrderNumber',
  'externalReference',
  'requestedDeliveryDate',
  'contactPersonName',
  'contactPersonNumber',
  'orderType',
  'orderReason',
  'quoteNumber',
  'projectId',
  'headerText',
  'shipToUid',
  'shipToAddress',
  'alternativeShipToAddress',
  'shipComplete',
  'shippingCondition',
  'forwardingAgent',
  'orderingPersonName',
  'orderingPersonEmail',
  'additionalEmails',
  'additionalText',
  'voucherCode',
  'lotBill',
  'completeDelivery',
  'specialConsideration',
  'items',
] as const satisfies readonly (keyof OrderCreateData)[];

/**
 * `false` — and therefore a compile error on the assignment below — as soon as `OrderCreateData`
 * declares a field `ORDER_CREATE_DATA_KEYS` does not list. `satisfies` alone only rejects a key that
 * is not on the type; this rejects a field that is not on the list.
 */
type OrderCreateDataKeysAreComplete =
  Exclude<keyof OrderCreateData, (typeof ORDER_CREATE_DATA_KEYS)[number]> extends never ? true : false;

export const ORDER_CREATE_DATA_KEYS_ARE_COMPLETE: OrderCreateDataKeysAreComplete = true;

/**
 * SAP-style name lines, not four company slots. `companyName` carries the organization when the
 * address names one and the recipient otherwise; an address naming both puts the person on
 * `companyName2`. See `signifyApiAction`'s `toOrderAddress`.
 */
export interface OrderAddress {
  companyName?: string;
  companyName2?: string;
  companyName3?: string;
  companyName4?: string;
  street: string;
  houseNumber?: string;
  town: string;
  district?: string;
  postalCode: string;
  regionIsoCode: string;
  countryIsoCode: string;
}

/**
 * Signify order line items may omit `customerReference`; this codebase always sends a string
 * (see workflow `signifyApiAction`, fallback `'0'` when empty).
 */
export interface OrderProduct {
  code: string;
  quantity: number;
  customerReference?: string;
  itemNumber?: number;
  configurationId?: string;
  requestedDeliveryDate?: string;
  externalLineReference?: string;
  expectedPrice?: number;
  itemText?: string;
  type?: string;
  deliveryPlant?: string;
  voucherCode?: string;
}

export interface SignifyOrderResponse {
  orderStatus: 'SUCCESS' | 'EXISTING';
  salesdocument: string;
  message?: string[];
  type?: string;
}

/**
 * Order Retrieval API Response Types
 */
export interface OrderSearchResult {
  count: string;
  value: OrderBasicHeader[];
}

export interface OrderBasicHeader {
  orderNumber: string;
  status: string;
  purchaseOrderNumber: string;
  externalReference: string;
  creationDate: string;
  orderType: string;
  netValue?: { value: number; currency: { iso: string } };
  customer?: { id: string; name: string };
}

/**
 * The document a communication log hangs off, named as validated segments rather
 * than as a path. `checkSignifyOrderStatus` runs on the admin SDK, where
 * `firestore.rules` never applies, so it builds the Firestore path itself and
 * authorizes that parent before reading a single bundle — a client-supplied path
 * would let any authenticated caller name any document.
 */
export type CheckSignifyOrderStatusParent =
  | { kind: 'form'; formId: string; versionId: string }
  | { kind: 'entityRecord'; entityId: string; entityRecordId: string };

/**
 * Identifies the one communication-log record an on-demand status check is asked
 * about: `parent` is the form version or entity record the log hangs off,
 * `fieldId` + `recordId` locate the entry inside its bundles. `recordId` is the
 * log entry's own id and is never the parent's `entityRecordId`. The check reads
 * that record's stored `purchaseOrderNumber` and `externalReference` and asks
 * Signify what it currently knows; nothing is written back.
 */
export type CheckSignifyOrderStatusRequest = {
  parent: CheckSignifyOrderStatusParent;
  fieldId: string;
  recordId: string;
};

/**
 * One order the order search returned that matched the record. Every candidate
 * that matched on either basis is reported, because a second order Signify holds
 * under the same purchase order number is the duplicate-retry a reader is looking
 * for; hiding it behind a stronger match would hide the answer.
 *
 * `matchedOn` says how strong that one order's match is: `externalReference` is
 * the client key the submit sent, `purchaseOrderNumber` only that the order
 * carries the same user-typed PO number. Neither identifies who placed the order. `status` is an
 * opaque vendor string with no documented vocabulary — display it, never branch
 * on it. `creationDate` is date-only, so it cannot order two attempts made on
 * the same day.
 */
export type SignifyOrderStatusMatch = {
  orderNumber: string;
  status: string;
  purchaseOrderNumber: string;
  externalReference: string;
  creationDate: string;
  matchedOn: 'externalReference' | 'purchaseOrderNumber';
};

/**
 * The answer an on-demand Signify order status check produces. The four cases are
 * never collapsed: `matched` asserts an order exists under this reference now,
 * `noMatch` asserts only that nothing in what the search returned matched — the
 * search's temporal and channel scope is undocumented, so it is not evidence the
 * order was never placed — `searchFailed` asserts nothing at all, and
 * `notSearchable` means no search was issued because the record stores no
 * purchase order number, the sole filter the order search accepts.
 *
 * `returnedCount` is how many orders the searched page carried, so a reader can
 * tell a no-match drawn from an empty page from one drawn from a full page.
 * `truncated` says that page was not the whole search — Signify's own
 * `OrderSearchResult.count` exceeded it, or the page came back full and `count`
 * was unusable — and a truncated no-match is a partial look, never
 * searched-and-absent. `reportedCount` is that vendor total when it parsed.
 */
export type SignifyOrderStatusCheck =
  | {
      result: 'matched';
      searchedPurchaseOrderNumber: string;
      externalReference?: string;
      returnedCount: number;
      reportedCount?: number;
      truncated: boolean;
      matches: SignifyOrderStatusMatch[];
    }
  | {
      result: 'noMatch';
      searchedPurchaseOrderNumber: string;
      externalReference?: string;
      returnedCount: number;
      reportedCount?: number;
      truncated: boolean;
    }
  | {
      result: 'searchFailed';
      searchedPurchaseOrderNumber: string;
      failure: string;
    }
  | {
      result: 'notSearchable';
      reason: string;
    };

export interface OrderHeaderResponse extends OrderBasicHeader {
  creditBlock?: boolean;
  deliveryBlock?: boolean;
  invoiceBlock?: boolean;
  projectName?: string;
  projectCode?: string;
  paymentTerm?: string;
  orderBasicCommission?: number;
  orderPremiumCommission?: number;
  orderTotalCommission?: number;
  shipToAddress?: { city: string; name: string; street: string; postCode: string; region: string };
  weight?: { unit: string; value: number };
  partners?: Array<{ function: string; name: string; id: string }>;
}

export interface OrderItemResponse {
  lineItemNum: number;
  materialCode: string;
  materialEntered: string;
  materialDesc: string;
  catalog: string;
  brand: string;
  fixtureType: string;
  orderqty: string;
  netPrice: SignifyMonetaryAmount;
  netValue: SignifyMonetaryAmount;
  expectedDelivery: string;
  shipDate?: string;
  shipped?: boolean;
  shipQty?: string;
  carrier?: string;
  deliveryNumber?: string;
  plant?: string;
  plantDesc?: string;
  externalReference?: string;
  externalLineReference?: string;
  customerReference?: string;
  invoice?: string;
  invoicedAmount?: SignifyMonetaryAmount;
  rejectReasonCode?: string;
  rejectReasonDesc?: string;
  orderCommissionBasic?: SignifyMonetaryAmount;
  orderCommissionOverage?: SignifyMonetaryAmount;
  deliveryUrl?: string;
  proNumber?: string;
  invoiceUrl?: string;
  invoicedQty?: SignifyQuantityAmount;
  invoiceBasicCommission?: SignifyMonetaryAmount;
  invoiceOverageCommission?: SignifyMonetaryAmount;
  sumInvoiceBasicCommission?: SignifyMonetaryAmount;
  sumInvoiceOverageCommission?: SignifyMonetaryAmount;
  sumInvoiceSalesAmount?: SignifyMonetaryAmount;
  deliveries?: SignifyOrderDelivery[];
}

export interface SignifyOrderDelivery {
  carrier?: string;
  deliveryNumber?: string;
  deliveryUrl?: string;
  invoice?: string;
  invoicedAmount?: SignifyMonetaryAmount;
  shipDate?: string;
  shipped?: boolean | string;
  proNumber?: string;
  invoicedQty?: SignifyQuantityAmount;
  invoiceBasicCommission?: SignifyMonetaryAmount;
  invoiceOverageCommission?: SignifyMonetaryAmount;
  sumInvoiceBasicCommission?: SignifyMonetaryAmount;
  sumInvoiceOverageCommission?: SignifyMonetaryAmount;
  sumInvoiceSalesAmount?: SignifyMonetaryAmount;
  shipments?: Array<{ proURL?: string; trackingNumber?: string }>;
}

export interface OrderItemsResponse {
  result: OrderItemResponse[];
}

/**
 * Invoice APIs (`b2b-invoice-standalone-service/v1`), typed from the vendor contract
 * `docs/signifyAPIs-2026_09_04/Invoices.json`, with its field names verbatim (including
 * `paymentTerrmCode` and `SAP__Origin`). The contract lists no required fields, so every field is
 * optional. Its fields are declared `string` although several of its examples are numbers, so a
 * reader comparing identifiers must not rely on the JS type.
 */
export type SignifyInvoiceCustomer = {
  distributionChannel?: string;
  id?: string;
  name?: string;
  companyName2?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  town?: string;
  district?: string;
  regionIsoCode?: string;
  countryIsoCode?: string;
};

export type SignifyInvoiceShipToAddress = {
  companyName?: string;
  companyName2?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  town?: string;
  district?: string;
  regionIsoCode?: string;
  countryIsoCode?: string;
};

/** One invoice as `invoice/search` lists it. `totalTax`, `poNumber` and `orderID` are CLS-only. */
export type SignifyInvoiceHeader = {
  SAP__Origin?: string;
  invoiceNumber?: string;
  billingDate?: string;
  billingType?: string;
  shippingCondition?: string;
  fiscalYear?: string;
  postingPeriod?: string;
  postingStatus?: string;
  incoterms1?: string;
  incoterms2?: string;
  paymentTerrmCode?: string;
  paymentTermDesc?: string;
  totalTax?: string;
  poNumber?: string;
  orderID?: string;
  customer?: SignifyInvoiceCustomer;
  shipToAddress?: SignifyInvoiceShipToAddress;
};

/** `GET invoice/search`. With no filter Signify returns one month of history up to today. */
export type SignifyInvoiceSearchResponse = {
  count?: number;
  value?: SignifyInvoiceHeader[];
};

/** Query parameters of `invoice/search` the reconciliation uses. `orderID`, when present, makes Signify ignore the others. */
export type SignifyInvoiceSearchParameters = {
  orderID?: string;
  distributorid?: string;
  billingDate?: string;
  pageSize?: string;
  offset?: string;
};

/**
 * One billed line. `refDoc` is the SAP delivery the line bills, and `OrderID` + `OrderItem` the
 * order line; `invoiceqtyValue` is the billed quantity in `invoiceqtyUnit`.
 */
export type SignifyInvoiceItem = {
  SAP__Origin?: string;
  invoiceNumber?: string;
  itemNumber?: string;
  OrderID?: string;
  OrderItem?: string;
  salesDoc?: string;
  salesDocItem?: string;
  invoiceqtyValue?: string;
  netvalueCurrency?: string;
  netvalueValue?: string;
  netPrice?: string;
  invoiceqtyUnit?: string;
  refDoc?: string;
  refItem?: string;
  catalogNumber?: string;
  externalLineReference?: string;
  brand?: string;
  materialCode?: string;
  materialDesc?: string;
  materialEntered?: string;
  shippingPoint?: string;
  orderEntryDate?: string;
};

export type SignifyInvoicePricing = {
  conditionTypeIdentifier?: string;
  currencyIso?: string;
  value?: number;
};

/**
 * `GET invoice/details/{invoiceNumber}` (auth header `x-lapis-token`). The contract types
 * `InvoiceItems` as one object while its name suggests a list, and no production reader settles
 * which; both are accepted here, and readers normalise with `signifyInvoiceItems`.
 */
export type SignifyInvoiceDetails = SignifyInvoiceHeader & {
  InvoiceItems?: SignifyInvoiceItem | SignifyInvoiceItem[];
  InvoicePricing?: SignifyInvoicePricing[];
  fixtureType?: string;
};

export interface SignifyQuoteDetailsResponse extends SignifyQuoteResponse {
  customerId: string;
  distributorId: string;
  customerName?: string;
  distributorName?: string;
  quoter?: string;
  quotername?: string;
  biddate?: string;
  expectedOrderDate?: string;
  headertext?: string;
  agentNotes?: string;
  attachments?: QuoteAttachment[];
  history?: QuoteRevision[];
}

export interface QuoteAttachment {
  id: string;
  name: string;
  size: number;
  uploadDate: string;
  url?: string;
}

export interface QuoteRevision {
  revision: number;
  createdDate: string;
  createdBy: string;
  changes?: string;
}

/**
 * Priority fields for displaying Signify product information
 */
export const SIGNIFY_PRODUCT_PRIORITY_FIELDS = [
  'name',
  'commercialCode',
  'code',
  'brand.name',
  'brand.code',
] as const;

export type SignifyProductPriorityField = typeof SIGNIFY_PRODUCT_PRIORITY_FIELDS[number];

/**
 * Formats Signify API field names for display
 */
export function formatFieldName(key: string): string {
  // Handle product fields
  if (key.startsWith('product.')) {
    const fieldName = key.replace('product.', '');
    // Handle nested properties like brand.name
    const parts = fieldName.split('.');
    const formatted = parts
      .map((part) => {
        // Convert camelCase to Title Case
        return part
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
      })
      .join(' ');
    return formatted;
  }
  // Handle SNAKE_CASE price type identifiers
  if (key.includes('_')) {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  // Handle camelCase keys (e.g. defaultPlannedDeliveryTime → Default Planned Delivery Time)
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Type guard to check if object has date-like properties
 */
function isDateLikeObject(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  
  const obj = value as Record<string, unknown>;
  return Boolean(
    obj.$isDayjsObject ||
    obj._isAMomentObject ||
    obj._isValid ||
    obj._d
  );
}

/**
 * Flattens nested Signify product objects to include properties like brand.name
 */
export function flattenApiObject<T extends Record<string, unknown>>(
  obj: T,
  prefix = ''
): Array<[string, unknown]> {
  const result: Array<[string, unknown]> = [];

  Object.entries(obj).forEach(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (value !== null && value !== undefined && value !== '') {
      // For nested objects like brand, successor, etc.
      // Check if it's a plain object (not Date, dayjs, moment, or Array)
      if (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date) &&
        !isDateLikeObject(value)
      ) {
        // Add nested properties
        result.push(...flattenApiObject(value as Record<string, unknown>, fullKey));
      } else {
        // Add simple values
        result.push([fullKey, value]);
      }
    }
  });

  return result;
}

 